import { useEffect, useState } from 'react';
import { db, withSyncMeta } from '@/offline/db';
import { enqueueSync } from '@/offline/sync';
import { useAuthStore } from '@/lib/auth-store';
import type { EPCR, EpcrAmendment, AmendmentType } from '@/types';
import { cn } from '@/lib/cn';
import {
  FilePen, Plus, Clock, AlertCircle, CheckCircle2, X,
  User, Lock as LockIcon,
} from 'lucide-react';

interface Props {
  epcr: EPCR;
}

const AMENDMENT_TYPES: { value: AmendmentType; label: string; description: string }[] = [
  { value: 'demographic_correction', label: 'Demographic Correction',
    description: 'Patient name, DOB, address, or ID corrected after handoff' },
  { value: 'clinical_clarification', label: 'Clinical Clarification',
    description: 'Additional detail about already-documented care' },
  { value: 'vital_sign_addition', label: 'Late Vital Signs',
    description: 'Vital set not documented during the original call' },
  { value: 'procedure_addition', label: 'Late Procedure Documentation',
    description: 'Procedure performed but not originally documented' },
  { value: 'medication_addition', label: 'Late Medication Documentation',
    description: 'Medication given but not originally documented' },
  { value: 'narrative_clarification', label: 'Narrative Addendum',
    description: 'Clarifying text appended to the clinical narrative' },
  { value: 'transmission_follow_up', label: 'Transmission Follow-up',
    description: 'Documentation of eventual delivery after delayed report handoff' },
  { value: 'other', label: 'Other', description: 'Anything else requiring an auditable correction' },
];

/**
 * Amendments tab — post-lock correction workflow (RI Compliance Spec §12.3).
 *
 * Amendments are append-only. They never modify the original report.
 * Each amendment is timestamped, attributed, reason-coded, and (optionally)
 * supervisor-approved. All amendments appear in the printable TOC and
 * are included in RIEMSIS submission as additive addenda.
 */
export function AmendmentsTab({ epcr }: Props) {
  const { user } = useAuthStore();
  const [amendments, setAmendments] = useState<EpcrAmendment[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<{
    amendment_type: AmendmentType;
    reason: string;
    narrative_text: string;
  }>({ amendment_type: 'demographic_correction', reason: '', narrative_text: '' });

  useEffect(() => {
    load();
  }, [epcr.id]);

  async function load() {
    const records = await db.epcr_amendments
      .where('epcr_id')
      .equals(epcr.id)
      .sortBy('authored_at')
      .catch(() => [] as EpcrAmendment[]);
    setAmendments(records);
  }

  async function saveAmendment() {
    if (!user) return;
    if (!form.reason.trim()) {
      alert('Reason for amendment is required.');
      return;
    }

    const amendment: EpcrAmendment = {
      id: crypto.randomUUID(),
      epcr_id: epcr.id,
      agency_id: epcr.agency_id,
      amendment_type: form.amendment_type,
      reason: form.reason.trim(),
      content: {},
      narrative_text: form.narrative_text.trim() || null,
      authored_by: user.id,
      authored_at: new Date().toISOString(),
      approved_by: null,
      approved_at: null,
      created_at: new Date().toISOString(),
    };

    await db.epcr_amendments.add(withSyncMeta(amendment));
    await enqueueSync('epcr_amendments', amendment.id, 'insert', amendment as unknown as Record<string, unknown>);

    setForm({ amendment_type: 'demographic_correction', reason: '', narrative_text: '' });
    setShowForm(false);
    await load();
  }

  const canAmend = epcr.locked; // Amendments only make sense after the original is locked

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FilePen size={20} className="text-brand-400" />
          <h3 className="text-lg font-semibold text-slate-200">Amendments & Corrections</h3>
          <span className="text-xs text-slate-500">
            ({amendments.length} {amendments.length === 1 ? 'amendment' : 'amendments'})
          </span>
        </div>
        {canAmend && !showForm && (
          <button onClick={() => setShowForm(true)} className="btn-primary gap-1 text-sm">
            <Plus size={14} /> New Amendment
          </button>
        )}
      </div>

      {/* Explainer */}
      <div className="rounded-lg border border-slate-700 bg-slate-900 p-3 text-xs text-slate-400 flex gap-2">
        <AlertCircle size={14} className="mt-0.5 shrink-0" />
        <div>
          Amendments are <strong>append-only corrections</strong> made after the report is finalized.
          The original report is preserved unchanged. Each amendment is timestamped, attributed, and
          available in the printable TOC and RIEMSIS submission. Per RI Protocol §12.3,
          legitimate post-handoff corrections go here — not as placeholder values in the original chart.
        </div>
      </div>

      {/* Not locked yet — can't amend */}
      {!canAmend && (
        <div className="rounded-lg border border-slate-700 bg-slate-900 p-6 text-center">
          <LockIcon size={32} className="mx-auto mb-2 text-slate-600" />
          <p className="text-slate-400 text-sm">
            Amendments become available after this report is locked by QA sign-off.
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Before lock, edit fields directly in each tab.
          </p>
        </div>
      )}

      {/* Amendment form */}
      {showForm && (
        <section className="rounded-lg border border-brand-500/30 bg-slate-800 p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-slate-200">New Amendment</h4>
            <button
              onClick={() => setShowForm(false)}
              className="rounded p-1 hover:bg-slate-700"
            >
              <X size={16} />
            </button>
          </div>

          <div>
            <label className="text-xs text-slate-400">Amendment type</label>
            <select
              value={form.amendment_type}
              onChange={(e) => setForm((f) => ({ ...f, amendment_type: e.target.value as AmendmentType }))}
              className="input-field"
            >
              {AMENDMENT_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-slate-500">
              {AMENDMENT_TYPES.find((t) => t.value === form.amendment_type)?.description}
            </p>
          </div>

          <div>
            <label className="text-xs text-slate-400">
              Reason for amendment <span className="text-ems-amber">*</span>
            </label>
            <textarea
              value={form.reason}
              onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
              className="input-field min-h-[4rem] resize-y"
              placeholder="e.g., Patient identified at receiving facility — updating demographic fields. Original report was submitted as Unknown pending identification."
              required
            />
          </div>

          <div>
            <label className="text-xs text-slate-400">
              Amendment content (clarifying text or corrected information)
            </label>
            <textarea
              value={form.narrative_text}
              onChange={(e) => setForm((f) => ({ ...f, narrative_text: e.target.value }))}
              className="input-field min-h-[5rem] resize-y"
              placeholder="Detailed amendment content. Will be appended to the narrative in reports and submissions."
            />
          </div>

          <div className="rounded-lg border border-amber-800 bg-amber-950/30 p-2 text-xs text-amber-300 flex gap-2">
            <AlertCircle size={14} className="mt-0.5 shrink-0" />
            <div>
              Amendments are <strong>immutable once saved</strong>. Review carefully before submitting.
              You will be attributed by name and timestamp.
            </div>
          </div>

          <div className="flex gap-2">
            <button onClick={saveAmendment} className="btn-primary gap-1">
              <FilePen size={14} /> Save Amendment
            </button>
            <button onClick={() => setShowForm(false)} className="btn-secondary">
              Cancel
            </button>
          </div>
        </section>
      )}

      {/* Existing amendments */}
      {amendments.length > 0 ? (
        <div className="space-y-3">
          {amendments.map((a) => (
            <AmendmentCard key={a.id} amendment={a} />
          ))}
        </div>
      ) : (
        canAmend && !showForm && (
          <div className="rounded-lg border border-slate-700 bg-slate-900 py-8 text-center">
            <FilePen size={32} className="mx-auto mb-2 text-slate-600" />
            <p className="text-slate-500">No amendments on this report</p>
            <p className="text-xs text-slate-600 mt-1">
              Original report is final as-locked.
            </p>
          </div>
        )
      )}
    </div>
  );
}

function AmendmentCard({ amendment }: { amendment: EpcrAmendment }) {
  const typeLabel = AMENDMENT_TYPES.find((t) => t.value === amendment.amendment_type)?.label ?? amendment.amendment_type;

  return (
    <div className="rounded-lg border border-slate-700 bg-slate-900 overflow-hidden">
      <header className="bg-slate-800 px-4 py-2 flex items-center gap-2">
        <FilePen size={14} className="text-brand-400" />
        <span className="text-sm font-semibold text-slate-200">{typeLabel}</span>
        <span className="ml-auto flex items-center gap-1 text-xs text-slate-500">
          <Clock size={12} />
          {new Date(amendment.authored_at).toLocaleString('en-US', {
            month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
          })}
        </span>
      </header>
      <div className="p-4 space-y-3">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500">Reason</div>
          <p className="mt-0.5 text-sm text-slate-200">{amendment.reason}</p>
        </div>
        {amendment.narrative_text && (
          <div>
            <div className="text-[10px] uppercase tracking-wide text-slate-500">Amendment</div>
            <p className="mt-0.5 text-sm text-slate-300 whitespace-pre-wrap">{amendment.narrative_text}</p>
          </div>
        )}
        <footer className="flex items-center gap-3 text-xs text-slate-500 border-t border-slate-800 pt-2">
          <span className="flex items-center gap-1">
            <User size={12} /> Authored by provider ID {amendment.authored_by.slice(0, 8)}
          </span>
          {amendment.approved_at ? (
            <span className="flex items-center gap-1 text-ems-green">
              <CheckCircle2 size={12} /> Supervisor approved {new Date(amendment.approved_at).toLocaleDateString()}
            </span>
          ) : (
            <span className={cn('italic')}>Pending supervisor review</span>
          )}
        </footer>
      </div>
    </div>
  );
}
